// POST /decks/finish ("Finish my deck"): the frozen wire contract of plan
// elegant-sprouting-riddle. Server field names stay snake_case (the house
// convention for REST response types, see src/api/types.ts) so the contract is
// greppable from the UI. Shared fixtures: ./__fixtures__/finish_contract_fixtures.json.
//
// Every call sends the Supabase bearer: the route refuses a missing one (401)
// and an anonymous Supabase user (403).
import { ApiError, apiPost } from './client'

export interface FinishCardInput {
  name: string
  quantity: number
}

export interface FinishRequest {
  commander: string
  partner: string | null
  /** The LIBRARY only, by physical quantity. Never the command zone. */
  cards: FinishCardInput[]
  /** Bracket 1-5. */
  power_level: number
  /** Additional spend only (locked cards never count). null: no budget. */
  budget: number | null
  /**
   * The collection, exactly as /decks/adjust-bracket takes it. Sent only when
   * a collection is loaded and its mode is not off; ABSENT otherwise (never
   * null, never 'off'), so a collection-less request is byte-identical to the
   * one sent before these fields existed.
   */
  ownership_mode?: FinishOwnershipMode
  collection_id?: string
  /** Owned names beyond the stored collection. Typed for the contract; the editor does not send it (Adjust does not). */
  additional_owned?: string[]
  /** One uuid per user action, reused only for that action's retries. */
  op_key: string
}

/** The ownership_mode wire codes /decks/finish shares with /decks/adjust-bracket. */
export type FinishOwnershipMode = 'off' | 'prefer' | 'only'

export interface FinishCardRow {
  name: string
  quantity_total: number
  quantity_kept: number
  /** quantity_total - quantity_kept. */
  quantity_added: number
  role: string
  /** "Your card" on any row with quantity_kept > 0. */
  reason: string | null
  /** The builder's reason for the GENERATED copies; non-null exactly when quantity_added > 0. */
  added_reason: string | null
  /** In the caller's collection. Additive: absent on a run without a collection (or an older API). */
  owned?: boolean
}

/** The run's collection rollup. Additive: absent without a collection (or on an older API). */
export interface FinishOwnership {
  mode: FinishOwnershipMode
  /** Added cards (copies) that are in the collection. */
  owned_added: number
}

export type FinishPeriod = 'lifetime' | 'day'

/** The quota block, on a 200 and in /pro/status's `finish`. */
export interface FinishQuota {
  used: number
  cap: number
  period: FinishPeriod
  resets_at: string | null
  is_pro: boolean
  remaining: number
}

export interface FinishResponse {
  /** Library-only text: no command zone, no section headers; sums to capacity. */
  decklist: string
  cards: FinishCardRow[]
  budget_summary: { budget: number | null; added_cost_usd: number }
  kept_count: number
  added_count: number
  removed_from_input: { name: string; reason: string }[]
  /** An ESTIMATE, shown on the review sheet only, never persisted. */
  bracket: { bracket: number; bracket_name: string }
  quota: FinishQuota
  replayed: boolean
  ownership?: FinishOwnership
  /** Builder notes (e.g. basic-land padding), shown as sent. Additive: absent or null reads as none. */
  warnings?: string[]
}

/** Every way a Finish can end without a result. */
export type FinishFailure =
  | { kind: 'auth_required' }
  | { kind: 'account_required' }
  /** 404: the API's flag is off (or the route is not deployed). */
  | { kind: 'not_available' }
  | { kind: 'too_many_cards' }
  | { kind: 'deck_full' }
  | { kind: 'invalid_cards'; cards: { name: string; reason: string }[] }
  /** 409 op_key_in_progress: retryable with the SAME op_key. */
  | { kind: 'in_progress' }
  | { kind: 'op_key_mismatch' }
  /** 410: the stored result was swept. Run again with a new op_key. */
  | { kind: 'expired' }
  /** 503: no charge. */
  | { kind: 'storage_unavailable' }
  /** The sent collection_id is unknown or expired (a 4xx, no charge). */
  | { kind: 'collection_missing' }
  /** 422 collection_too_small: Only mode would mostly pad with basics. No charge. */
  | { kind: 'collection_too_small'; owned_added: number; padded_basics: number }
  | { kind: 'quota'; reason: 'free_lifetime' | 'pro_daily'; used: number; cap: number; is_pro: boolean; resets_at: string | null }
  /** 500 finish_failed, or any status this client does not know: refunded. */
  | { kind: 'failed' }
  /**
   * A 200 whose body is not the contract's shape: never applied, shown as a
   * failure, but the server may hold a result, so its op is kept for Resume.
   */
  | { kind: 'malformed_response' }
  /** No HTTP answer at all (a thrown fetch, a timeout, a gateway 502/504): retryable. */
  | { kind: 'network' }

export type FinishOutcome =
  | { ok: true; data: FinishResponse }
  | { ok: false; failure: FinishFailure }

/* eslint-disable i18next/no-literal-string -- wire codes and the route path */
const PATH = '/decks/finish'

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
const strOrNull = (v: unknown): string | null => (typeof v === 'string' ? v : null)

/** The error object of a body: top level `{"error": ...}` or FastAPI's `{"detail": {...}}`. */
function errorBody(text: string | undefined): Record<string, unknown> | null {
  if (!text) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return null
  }
  if (!isObj(parsed)) return null
  if (typeof parsed.error === 'string') return parsed
  if (isObj(parsed.detail) && typeof parsed.detail.error === 'string') return parsed.detail
  if (typeof parsed.detail === 'string') return { error: parsed.detail }
  return null
}

function decodeQuota(v: unknown): FinishQuota | null {
  if (!isObj(v)) return null
  if (!isNum(v.used) || !isNum(v.cap)) return null
  const period: FinishPeriod = v.period === 'day' ? 'day' : 'lifetime'
  return {
    used: v.used,
    cap: v.cap,
    period,
    resets_at: strOrNull(v.resets_at),
    is_pro: v.is_pro === true,
    remaining: isNum(v.remaining) ? v.remaining : Math.max(0, v.cap - v.used),
  }
}

/** /pro/status's optional `finish` block, read leniently: anything malformed reads as absent. */
export function decodeFinishQuota(v: unknown): FinishQuota | null {
  return decodeQuota(v)
}

function decodeRow(v: unknown): FinishCardRow | null {
  if (!isObj(v) || typeof v.name !== 'string' || !v.name.trim()) return null
  if (!isNum(v.quantity_total) || !isNum(v.quantity_kept)) return null
  const added = isNum(v.quantity_added) ? v.quantity_added : v.quantity_total - v.quantity_kept
  return {
    name: v.name,
    quantity_total: v.quantity_total,
    quantity_kept: v.quantity_kept,
    quantity_added: added,
    role: typeof v.role === 'string' && v.role ? v.role : 'other',
    reason: strOrNull(v.reason),
    added_reason: strOrNull(v.added_reason),
    ...(typeof v.owned === 'boolean' ? { owned: v.owned } : {}),
  }
}

/** The optional `ownership` block; anything malformed reads as absent. */
function decodeOwnership(v: unknown): FinishOwnership | null {
  if (!isObj(v) || !isNum(v.owned_added) || v.owned_added < 0) return null
  const mode: FinishOwnershipMode | null = v.mode === 'off' || v.mode === 'prefer' || v.mode === 'only' ? v.mode : null
  if (!mode) return null
  return { mode, owned_added: v.owned_added }
}

/**
 * A 200 body, validated. null when it is not the contract's shape (no
 * decklist, no cards array, no quota): such a body is never applied.
 */
export function decodeFinishResponse(v: unknown): FinishResponse | null {
  if (!isObj(v) || typeof v.decklist !== 'string' || !Array.isArray(v.cards)) return null
  const quota = decodeQuota(v.quota)
  if (!quota) return null
  const cards: FinishCardRow[] = []
  for (const raw of v.cards) {
    const row = decodeRow(raw)
    if (!row) return null
    cards.push(row)
  }
  const bs = isObj(v.budget_summary) ? v.budget_summary : {}
  const br = isObj(v.bracket) ? v.bracket : {}
  return {
    decklist: v.decklist,
    cards,
    budget_summary: {
      budget: isNum(bs.budget) ? bs.budget : null,
      added_cost_usd: isNum(bs.added_cost_usd) ? bs.added_cost_usd : 0,
    },
    kept_count: isNum(v.kept_count) ? v.kept_count : cards.reduce((n, c) => n + c.quantity_kept, 0),
    added_count: isNum(v.added_count) ? v.added_count : cards.reduce((n, c) => n + c.quantity_added, 0),
    removed_from_input: Array.isArray(v.removed_from_input)
      ? v.removed_from_input.filter(isObj).map(r => ({ name: String(r.name ?? ''), reason: String(r.reason ?? '') })).filter(r => r.name)
      : [],
    bracket: { bracket: isNum(br.bracket) ? br.bracket : 0, bracket_name: typeof br.bracket_name === 'string' ? br.bracket_name : '' },
    quota,
    replayed: v.replayed === true,
    ...(() => {
      const ownership = decodeOwnership(v.ownership)
      return ownership ? { ownership } : {}
    })(),
    ...(() => {
      const warnings = Array.isArray(v.warnings)
        ? v.warnings.filter((w): w is string => typeof w === 'string' && w.trim() !== '')
        : []
      return warnings.length > 0 ? { warnings } : {}
    })(),
  }
}

/** A `code` field at the top level or under `detail` (the API's structured 4xx shape). */
function bodyCode(text: string | undefined): string | null {
  if (!text) return null
  try {
    const parsed: unknown = JSON.parse(text)
    if (!isObj(parsed)) return null
    if (typeof parsed.code === 'string') return parsed.code
    if (isObj(parsed.detail) && typeof parsed.detail.code === 'string') return parsed.detail.code
    if (isObj(parsed.detail) && typeof parsed.detail.error === 'string') return parsed.detail.error
    if (typeof parsed.error === 'string') return parsed.error
  } catch {
    return null
  }
  return null
}

/** FastAPI's own 404 detail: an unknown route, or the flag-off `HTTPException(404)`. */
const DEFAULT_404_DETAIL = 'Not Found'

/**
 * An HTTP failure (status + body text) as a FinishFailure. `sentCollection`:
 * the request carried a collection_id, so a 404 that is NOT FastAPI's bare
 * route/flag-off 404 is the collection lookup failing (/decks/adjust-bracket
 * raises 404 err.collection.not_found_reimport there, a sentence).
 */
export function classifyFinishHttp(status: number, bodyText?: string, sentCollection = false): FinishFailure {
  const body = errorBody(bodyText)
  const code = typeof body?.error === 'string' ? body.error : null
  if (status >= 400 && status < 500 && bodyCode(bodyText) === 'collection_not_found') return { kind: 'collection_missing' }
  switch (status) {
    case 401: return { kind: 'auth_required' }
    case 403: return { kind: 'account_required' }
    case 404:
      if (sentCollection && code && code !== DEFAULT_404_DETAIL) return { kind: 'collection_missing' }
      return { kind: 'not_available' }
    case 410: return { kind: 'expired' }
    case 409:
      if (code === 'op_key_mismatch') return { kind: 'op_key_mismatch' }
      return { kind: 'in_progress' }
    case 422:
      if (code === 'too_many_cards') return { kind: 'too_many_cards' }
      if (code === 'deck_full') return { kind: 'deck_full' }
      if (code === 'collection_too_small') {
        return {
          kind: 'collection_too_small',
          owned_added: isNum(body?.owned_added) ? body.owned_added : 0,
          padded_basics: isNum(body?.padded_basics) ? body.padded_basics : 0,
        }
      }
      if (code === 'invalid_cards') {
        const cards = Array.isArray(body?.cards)
          ? (body.cards as unknown[]).filter(isObj).map(c => ({ name: String(c.name ?? ''), reason: String(c.reason ?? '') })).filter(c => c.name)
          : []
        return { kind: 'invalid_cards', cards }
      }
      return { kind: 'failed' }
    case 429: {
      const reason = body?.reason === 'pro_daily' ? 'pro_daily' : 'free_lifetime'
      return {
        kind: 'quota',
        reason,
        used: isNum(body?.used) ? body.used : 0,
        cap: isNum(body?.cap) ? body.cap : 0,
        // A pro_daily refusal is a Pro caller whatever the flag says.
        is_pro: body?.is_pro === true || reason === 'pro_daily',
        resets_at: strOrNull(body?.resets_at),
      }
    }
    case 503:
      return { kind: 'storage_unavailable' }
    // A gateway that never reached the app: nothing ran, so it is as
    // retryable as a dropped connection.
    case 502:
    case 504:
      return { kind: 'network' }
    default:
      return { kind: 'failed' }
  }
}

/** Anything apiPost threw, as a FinishFailure. */
export function classifyFinishError(err: unknown, sentCollection = false): FinishFailure {
  if (err instanceof ApiError) return classifyFinishHttp(err.status, err.body, sentCollection)
  return { kind: 'network' }
}
/* eslint-enable i18next/no-literal-string */

/** The retryable failures: same op_key and payload, at most 3 more times. */
export function isRetryableFinishFailure(f: FinishFailure): boolean {
  return f.kind === 'network' || f.kind === 'in_progress'
}

/**
 * One POST /decks/finish. Never throws: every outcome is a value. A caller
 * abort surfaces as `network` (the caller knows it aborted).
 */
export async function postFinishDeck(req: FinishRequest, bearerToken: string, signal?: AbortSignal): Promise<FinishOutcome> {
  let raw: unknown
  try {
    raw = await apiPost<unknown>(PATH, req, { bearerToken, signal })
  } catch (err) {
    return { ok: false, failure: classifyFinishError(err, typeof req.collection_id === 'string' && req.collection_id !== '') }
  }
  const data = decodeFinishResponse(raw)
  if (!data) return { ok: false, failure: { kind: 'malformed_response' } }
  return { ok: true, data }
}
